import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import Navigation from "../components/Navigation";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Trophy,
  Star,
  Flame,
  Target,
  Medal,
  Crown,
  Zap,
  Heart,
  MessageCircle,
  Calendar,
  Wind,
  Sun,
  Users,
  Award,
  TrendingUp,
  CheckCircle,
  Lock,
  Sparkles,
  Footprints,
  Rocket,
  BookOpen,
  Megaphone,
  BadgeCheck,
  Compass,
  Wallet,
  Dumbbell,
  ChevronRight,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";

const ICON_MAP = {
  footprints: Footprints,
  target: Target,
  trophy: Trophy,
  rocket: Rocket,
  crown: Crown,
  flame: Flame,
  fire: Flame,
  star: Star,
  medal: Medal,
  heart: Heart,
  sparkles: Sparkles,
  "book-open": BookOpen,
  megaphone: Megaphone,
  "message-circle": MessageCircle,
  wind: Wind,
  cloud: Wind,
  sun: Sun,
  sunrise: Sun,
  users: Users,
  "calendar-check": Calendar,
  mic: Megaphone,
  wallet: Wallet,
  "piggy-bank": Wallet,
  "heart-pulse": Heart,
  dumbbell: Dumbbell,
  zap: Zap,
  "badge-check": BadgeCheck,
  compass: Compass,
};

const TIER_COLORS = {
  Beginner: "bg-stone-200 text-stone-700",
  Apprentice: "bg-green-100 text-green-700",
  Achiever: "bg-blue-100 text-blue-700",
  Expert: "bg-purple-100 text-purple-700",
  Master: "bg-amber-100 text-amber-700",
  Champion: "bg-gradient-to-r from-primary to-destructive text-white",
  Legend: "bg-gradient-to-r from-yellow-400 to-amber-600 text-white",
};

const TIER_ORDER = ["Beginner", "Apprentice", "Achiever", "Expert", "Master", "Champion", "Legend"];

const CATEGORY_LABELS = {
  life: "Life",
  financial: "Financial",
  spiritual: "Spiritual",
  family: "Family",
  health: "Health",
  career: "Career",
  social: "Social",
  learning: "Learning",
  wellness: "Wellness",
  community: "Community",
};

export default function Achievements() {
  const { user } = useAuth();
  const [progress, setProgress] = useState(null);
  const [badges, setBadges] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [tierInfo, setTierInfo] = useState(null);
  const [pointValues, setPointValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("overall");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [progressRes, badgesRes, leaderboardRes, tierRes, pointsRes] = await Promise.all([
        axios.get(`${API}/progress`),
        axios.get(`${API}/badges`),
        axios.get(`${API}/leaderboard`),
        axios.get(`${API}/tiers`),
        axios.get(`${API}/point-values`),
      ]);
      
      setProgress(progressRes.data);
      setBadges(badgesRes.data.badges);
      setLeaderboard(leaderboardRes.data.leaderboard);
      setTierInfo(tierRes.data);
      setPointValues(pointsRes.data.point_values);
    } catch (error) {
      toast.error("Failed to load achievements");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryLeaderboard = async (category) => {
    setSelectedCategory(category);
    try {
      const url = category === "overall" 
        ? `${API}/leaderboard` 
        : `${API}/leaderboard?category=${category}`;
      const response = await axios.get(url);
      setLeaderboard(response.data.leaderboard);
    } catch (error) {
      toast.error("Failed to load leaderboard");
    }
  };

  const getIcon = (iconName) => {
    return ICON_MAP[iconName] || Star;
  };

  const earnedBadges = badges.filter((b) => b.earned);
  const unearnedBadges = badges.filter((b) => !b.earned);

  // Group badges by category
  const badgesByCategory = badges.reduce((acc, badge) => {
    if (!acc[badge.category]) acc[badge.category] = [];
    acc[badge.category].push(badge);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-20 h-20 rounded-full bg-muted"></div>
          <div className="h-6 w-40 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-primary/10 to-purple-500/10"></div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-primary flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Trophy className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-2">
              Achievements
            </h1>
            <p className="text-muted-foreground text-lg">
              Track your progress from Beginner to Legend
            </p>
          </div>

          {/* Main Progress Card */}
          {progress && (
            <div className="card-happy max-w-2xl mx-auto">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="text-center md:text-left">
                  <div className="text-5xl font-bold text-primary mb-2">
                    {progress.total_points.toLocaleString()}
                  </div>
                  <p className="text-muted-foreground">Total Points</p>
                </div>
                
                <div className="flex-1 w-full">
                  <div className="flex items-center justify-between mb-2">
                    <Badge className={`${TIER_COLORS[progress.overall_tier]} px-4 py-1 text-lg`}>
                      {progress.overall_tier}
                    </Badge>
                    {progress.next_tier && (
                      <span className="text-sm text-muted-foreground">
                        {progress.points_to_next_tier} pts to {progress.next_tier}
                      </span>
                    )}
                  </div>
                  <Progress 
                    value={progress.next_tier ? 
                      ((tierInfo?.tiers[progress.overall_tier] || 0) / (tierInfo?.tiers[progress.next_tier] || 1)) * 100 + 
                      ((progress.total_points - (tierInfo?.tiers[progress.overall_tier] || 0)) / 
                      ((tierInfo?.tiers[progress.next_tier] || 1) - (tierInfo?.tiers[progress.overall_tier] || 0))) * 
                      (100 - ((tierInfo?.tiers[progress.overall_tier] || 0) / (tierInfo?.tiers[progress.next_tier] || 1)) * 100)
                      : 100} 
                    className="h-3" 
                  />
                </div>
              </div>

              {/* Streak & Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-border">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-2xl font-bold text-orange-500">
                    <Flame className="w-5 h-5" />
                    {progress.current_streak}
                  </div>
                  <p className="text-xs text-muted-foreground">Day Streak</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">{earnedBadges.length}</div>
                  <p className="text-xs text-muted-foreground">Badges Earned</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-500">{progress.stats.goals_completed}</div>
                  <p className="text-xs text-muted-foreground">Goals Done</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-500">{progress.stats.goals_with_proof}</div>
                  <p className="text-xs text-muted-foreground">With Proof</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="progress" className="space-y-8">
          <TabsList className="grid w-full grid-cols-4 max-w-lg mx-auto">
            <TabsTrigger value="progress">Progress</TabsTrigger>
            <TabsTrigger value="badges">Badges</TabsTrigger>
            <TabsTrigger value="leaderboard">Ranks</TabsTrigger>
            <TabsTrigger value="points">Points</TabsTrigger>
          </TabsList>

          {/* Progress Tab - Category Tiers */}
          <TabsContent value="progress" className="space-y-6">
            <h2 className="text-2xl font-semibold">Category Progress</h2>
            <p className="text-muted-foreground">
              Level up in each category by completing goals with proof
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              {progress && Object.entries(progress.category_progress).map(([cat, data]) => (
                <div key={cat} className="card-happy">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold capitalize">{CATEGORY_LABELS[cat] || cat}</h3>
                    <Badge className={TIER_COLORS[data.tier]}>{data.tier}</Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-muted-foreground">{data.points} pts</span>
                    {data.next_tier && (
                      <span className="text-muted-foreground">
                        {data.points_to_next} to {data.next_tier}
                      </span>
                    )}
                  </div>
                  <Progress 
                    value={data.next_tier ? 
                      (data.points / (data.points + data.points_to_next)) * 100 : 100} 
                    className="h-2" 
                  />
                </div>
              ))}
            </div>

            {/* Tier Roadmap */}
            <div className="card-happy mt-8">
              <h3 className="font-semibold mb-4">Tier Roadmap</h3>
              <div className="flex items-center justify-between overflow-x-auto gap-2 pb-2">
                {TIER_ORDER.map((tier) => {
                  const threshold = tierInfo?.tiers[tier] || 0;
                  const isAchieved = progress && progress.total_points >= threshold;
                  const isCurrent = progress?.overall_tier === tier;
                  
                  return (
                    <div
                      key={tier}
                      className={`flex flex-col items-center min-w-[90px] transition-all ${
                        isCurrent ? "scale-110" : isAchieved ? "" : "opacity-50"
                      }`}
                    >
                      <div
                        className={`w-14 h-14 rounded-full flex items-center justify-center mb-2 ${
                          TIER_COLORS[tier]
                        } ${isCurrent ? "ring-4 ring-primary/30" : ""}`}
                      >
                        {isAchieved ? (
                          <CheckCircle className="w-6 h-6" />
                        ) : (
                          <Lock className="w-5 h-5" />
                        )}
                      </div>
                      <span className="text-sm font-medium">{tier}</span>
                      <span className="text-xs text-muted-foreground">{threshold} pts</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          {/* Badges Tab */}
          <TabsContent value="badges" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-semibold">Badges</h2>
                <p className="text-muted-foreground">
                  {earnedBadges.length} of {badges.length} earned
                </p>
              </div>
            </div>

            {/* Earned Badges */}
            {earnedBadges.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-primary" />
                  Earned ({earnedBadges.length})
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {earnedBadges.map((badge) => {
                    const Icon = getIcon(badge.icon);
                    return (
                      <div
                        key={badge.id}
                        className="card-happy text-center bg-gradient-to-br from-primary/5 to-amber-500/5"
                      >
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-amber-500 flex items-center justify-center mx-auto mb-3">
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        <h4 className="font-semibold">{badge.name}</h4>
                        <p className="text-xs text-muted-foreground mt-1">{badge.description}</p>
                        <Badge className="mt-2 bg-primary/10 text-primary">+{badge.points} pts</Badge>
                        {badge.earned_at && (
                          <p className="text-xs text-muted-foreground mt-2">
                            {format(parseISO(badge.earned_at), "MMM d, yyyy")}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Badges by Category */}
            {Object.entries(badgesByCategory).map(([category, categoryBadges]) => {
              const unearnedInCategory = categoryBadges.filter((b) => !b.earned);
              if (unearnedInCategory.length === 0) return null;
              
              return (
                <div key={category}>
                  <h3 className="text-lg font-semibold mb-4 capitalize flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    {category.replace("_", " ")} ({unearnedInCategory.length} remaining)
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {unearnedInCategory.map((badge) => {
                      const Icon = getIcon(badge.icon);
                      return (
                        <div
                          key={badge.id}
                          className="card-happy text-center opacity-60"
                        >
                          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
                            <Icon className="w-8 h-8 text-muted-foreground" />
                          </div>
                          <h4 className="font-semibold text-muted-foreground">{badge.name}</h4>
                          <p className="text-xs text-muted-foreground mt-1">{badge.description}</p>
                          <Badge variant="outline" className="mt-2">+{badge.points} pts</Badge>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </TabsContent>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-semibold">Leaderboard</h2>
            </div>

            {/* Category Filter */}
            <ScrollArea className="w-full whitespace-nowrap pb-2">
              <div className="flex gap-2">
                <button
                  onClick={() => fetchCategoryLeaderboard("overall")}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === "overall"
                      ? "bg-primary text-white"
                      : "bg-muted hover:bg-muted/80"
                  }`}
                >
                  Overall
                </button>
                {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => fetchCategoryLeaderboard(key)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                      selectedCategory === key
                        ? "bg-primary text-white"
                        : "bg-muted hover:bg-muted/80"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </ScrollArea>

            {/* Leaderboard List */}
            <div className="space-y-3">
              {leaderboard.length === 0 ? (
                <div className="card-happy text-center py-12">
                  <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No rankings yet in this category</p>
                </div>
              ) : (
                leaderboard.map((entry, index) => {
                  const isCurrentUser = entry.user_id === user?.id;
                  return (
                    <div
                      key={entry.user_id}
                      className={`card-happy flex items-center gap-4 ${
                        isCurrentUser ? "ring-2 ring-primary bg-primary/5" : ""
                      }`}
                    >
                      {/* Rank */}
                      <div className="w-12 text-center">
                        {entry.rank <= 3 ? (
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto ${
                              entry.rank === 1
                                ? "bg-yellow-400 text-yellow-900"
                                : entry.rank === 2
                                ? "bg-gray-300 text-gray-700"
                                : "bg-amber-600 text-amber-100"
                            }`}
                          >
                            {entry.rank === 1 ? (
                              <Crown className="w-5 h-5" />
                            ) : (
                              <Medal className="w-5 h-5" />
                            )}
                          </div>
                        ) : (
                          <span className="text-2xl font-bold text-muted-foreground">
                            {entry.rank}
                          </span>
                        )}
                      </div>

                      {/* User */}
                      <Avatar className="w-12 h-12">
                        <AvatarImage src={entry.user_photo} />
                        <AvatarFallback className="bg-primary text-white">
                          {entry.user_name?.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>

                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold">
                            {entry.user_name}
                            {isCurrentUser && (
                              <span className="text-primary ml-1">(You)</span>
                            )}
                          </h4>
                          <Badge className={TIER_COLORS[entry.tier]} variant="secondary">
                            {entry.tier}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <span>{entry.badges_count} badges</span>
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="text-2xl font-bold text-primary">
                          {entry.total_points.toLocaleString()}
                        </div>
                        <p className="text-xs text-muted-foreground">points</p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </TabsContent>

          {/* Points Tab */}
          <TabsContent value="points" className="space-y-6">
            <h2 className="text-2xl font-semibold">How to Earn Points</h2>
            <p className="text-muted-foreground">
              Complete activities to earn points and level up
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {Object.entries(pointValues).map(([action, points]) => {
                const labels = {
                  goal_completed_with_proof: { label: "Complete Goal with Proof", icon: BadgeCheck },
                  goal_completed: { label: "Complete Goal", icon: Target },
                  post_created: { label: "Create Post", icon: BookOpen },
                  reaction_received: { label: "Receive Reaction", icon: Heart },
                  reaction_given: { label: "Give Reaction", icon: Heart },
                  comment_created: { label: "Write Comment", icon: MessageCircle },
                  comment_received: { label: "Receive Comment", icon: MessageCircle },
                  event_attended: { label: "Attend Event", icon: Calendar },
                  event_hosted: { label: "Host Event", icon: Users },
                  breathing_session: { label: "Breathing Session", icon: Wind },
                  gratitude_entry: { label: "Gratitude Entry", icon: Sun },
                  daily_login: { label: "Daily Login", icon: Zap },
                  login_streak_7: { label: "7-Day Streak Bonus", icon: Flame },
                  login_streak_30: { label: "30-Day Streak Bonus", icon: Flame },
                  profile_completed: { label: "Complete Profile", icon: Users },
                  first_post: { label: "First Post Bonus", icon: Sparkles },
                  first_goal: { label: "First Goal Bonus", icon: Sparkles },
                };
                
                const info = labels[action] || { label: action.replace(/_/g, " "), icon: Star };
                const Icon = info.icon;
                
                return (
                  <div key={action} className="card-happy flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <span className="font-medium capitalize">{info.label}</span>
                    </div>
                    <Badge className="bg-primary/10 text-primary text-lg px-3">
                      +{points}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <Navigation />
    </div>
  );
}
